import { userAPI } from "../UserAPI";

import { useTranslation } from "react-i18next";
import { getCodeList } from 'country-list';
import { useModalContext } from "./ModalProvider";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface Properties {
    className?: string;
    address: any;
    addressChanged: (address: any) => void;
}

/** Pflichtfeld-Stern wie in den übrigen Formularen. */
const RequiredMark = () => <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>;

export default function AddressEntry(properties: Properties) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const codeList = getCodeList();

    return <div className={cn('flex flex-col gap-4', properties.className)}>
        <div className="flex flex-wrap gap-4">
            <div className="flex min-w-0 flex-1 basis-64 flex-col gap-1.5">
                <Label htmlFor="addressStreetField">{t('addressEntry.street')}<RequiredMark /></Label>
                <Input id="addressStreetField" value={properties.address.street ?? ''} onChange={(event) => {
                    properties.addressChanged({ ...properties.address, street: event.target.value });
                }} />
            </div>
            <div className="flex w-24 shrink-0 flex-col gap-1.5">
                <Label htmlFor="addressStreetNumberField">{t('addressEntry.streetNumber')}<RequiredMark /></Label>
                <Input id="addressStreetNumberField" value={properties.address.streetNumber ?? ''} onChange={(event) => {
                    properties.addressChanged({ ...properties.address, streetNumber: event.target.value });
                }} />
            </div>
        </div>
        <div className="flex flex-wrap gap-4">
            <div className="flex w-28 shrink-0 flex-col gap-1.5">
                <Label htmlFor="addressPostalCodeField">{t('addressEntry.postalCode')}<RequiredMark /></Label>
                <Input id="addressPostalCodeField" value={properties.address.postalCode ?? ''} onChange={async (event) => {
                    const postalCode = event.target.value.trim();

                    properties.addressChanged({ ...properties.address, postalCode });

                    // Only call API for German postal codes
                    if (postalCode && postalCode.length === 5 && !isNaN(postalCode as any) && properties.address.country === 'DE') {
                        try {
                            const result = await userAPI.getPostalCode(postalCode);

                            console.log('Result >>>', result);

                            if (result) {
                                properties.addressChanged({ ...properties.address, postalCode: result.postalCode, city: result.city });
                            }
                        } catch (error) {
                            pushError('Fehler beim Abrufen der Postleitzahl');
                        }
                    }
                }} />
            </div>
            <div className="flex min-w-0 flex-1 basis-64 flex-col gap-1.5">
                <Label htmlFor="addressCityField">{t('addressEntry.city')}<RequiredMark /></Label>
                <Input id="addressCityField" value={properties.address.city ?? ''} onChange={(event) => {
                    properties.addressChanged({ ...properties.address, city: event.target.value });
                }} />
            </div>
        </div>
        <div className="flex max-w-sm flex-col gap-1.5">
            <Label htmlFor="addressCountryField">{t('addressEntry.country')}</Label>
            <Select
                defaultValue="DE"
                value={properties.address.country}
                onValueChange={(country) => {
                    properties.addressChanged({ ...properties.address, country });
                }}
            >
                <SelectTrigger id="addressCountryField">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                    {Object.keys(codeList).map((key: any) => (
                        <SelectItem key={key.toUpperCase()} value={key.toUpperCase()}>{codeList[key]}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    </div>;
}
